import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin, Send } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/gomill-data";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Look out for fresh updates.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't subscribe right now.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-gomill-green text-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <img src={BRAND.logoWhite} alt="GoMill" className="h-20 md:h-24 w-auto object-contain" />
            </Link>
            <h3 className="font-serif text-4xl md:text-5xl leading-tight mb-4">Guilt-free food.<br/>Wherever you are.</h3>
            <p className="text-white/70 max-w-md mb-6">
              Millet-led meals made fresh, delivered fast by our mobile kitchens and kiosks. Clean food. Trusted brand. Always near you.
            </p>

            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full px-5 py-3 bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-gomill-mustard"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gomill-mustard hover:bg-gomill-mustardDark text-gomill-green font-semibold transition-colors disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-gomill-mustard font-semibold mb-4">Explore</div>
            <ul className="space-y-2">
              {NAV_LINKS.slice(0, 4).map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/80 hover:text-gomill-mustard text-sm">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-gomill-mustard font-semibold mb-4">Business</div>
            <ul className="space-y-2">
              {NAV_LINKS.slice(4).map((l) => (
                <li key={l.to}><Link to={l.to} className="text-white/80 hover:text-gomill-mustard text-sm">{l.label}</Link></li>
              ))}
              <li><Link to="/privacy" className="text-white/80 hover:text-gomill-mustard text-sm">Privacy Policy</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-gomill-mustard font-semibold mb-4">Reach Us</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-gomill-mustard"/> {BRAND.email}</li>
              <li className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-gomill-mustard"/> {BRAND.phone}</li>
              <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-gomill-mustard"/> {BRAND.address}</li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              <a href={BRAND.socials.instagram} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-2.5 pl-1 pr-4 py-1 rounded-full bg-white/10 hover:bg-gomill-mustard hover:text-gomill-green transition-colors" data-testid="social-instagram">
                <span className="w-8 h-8 rounded-full bg-white/15 group-hover:bg-gomill-green/15 inline-flex items-center justify-center">
                  <Instagram className="w-4 h-4"/>
                </span>
                <span className="text-sm font-semibold">Follow us on Instagram</span>
              </a>
            </div>

            {/* EBG Group badge */}
            <a
              href="https://theebg.com"
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-4 rounded-2xl border border-gomill-mustard/70 hover:border-gomill-mustard pl-3 pr-5 py-3 transition-colors max-w-[300px]"
              data-testid="ebg-group-badge"
            >
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_gomill-healthy-bites/artifacts/q5828f8r_EBG%20Logo.png"
                alt="EBG Group"
                className="w-24 h-24 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div className="text-white text-[13px] leading-snug">
                  An <span className="font-semibold" style={{ color: "#C9A24B" }}>EBG</span> Group<br/>Company
                </div>
                <div className="text-white/60 text-[11px] mt-1 inline-flex items-center gap-1">
                  theebg.com <span aria-hidden>↗</span>
                </div>
              </div>
            </a>
          </div>
        </div>

        {/* SDG alignment section */}
        <div className="mt-16 pt-10 border-t border-white/10" data-testid="footer-sdg">
          {/* Header: SDG logo + tagline */}
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8 mb-8" data-testid="sdg-intro">
            <img
              src="https://customer-assets.emergentagent.com/job_gomill-healthy-bites/artifacts/wkjswyfl_sdg%20logo.png"
              alt="UN Sustainable Development Goals"
              className="h-16 md:h-20 w-auto object-contain shrink-0"
              data-testid="sdg-logo"
            />
            <div className="md:border-l md:border-white/15 md:pl-8 max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-gomill-mustard mb-1.5">Our Pledge</div>
              <p className="text-white/85 text-sm md:text-[15px] leading-relaxed">
                We proudly stand with the United Nations <span className="text-white font-semibold">Sustainable Development Goals</span> — fuelling a healthier, fairer, and more sustainable tomorrow with every millet bowl we serve.
              </p>
            </div>
          </div>

          {/* 3 SDG cards spanning full width */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                n: 2,
                title: "Zero Hunger",
                desc: "Promoting nutritious, millet-based meals that support healthier communities.",
                img: "https://customer-assets.emergentagent.com/job_gomill-healthy-bites/artifacts/v3i0jzrt_Goal%202.png",
              },
              {
                n: 3,
                title: "Good Health and Well-being",
                desc: "Nutritious food choices for a healthier and happier life.",
                img: "https://customer-assets.emergentagent.com/job_gomill-healthy-bites/artifacts/d6t5qabm_Goal%203.png",
              },
              {
                n: 12,
                title: "Responsible Consumption and Production",
                desc: "Mindful sourcing, minimal waste, and sustainable practices.",
                img: "https://customer-assets.emergentagent.com/job_gomill-healthy-bites/artifacts/s7bfsvvz_Goal%2012.png",
              },
            ].map((g) => (
              <a
                key={g.n}
                href={`https://sdgs.un.org/goals/goal${g.n}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`sdg-${g.n}`}
                title={`SDG ${g.n}: ${g.title}`}
                className="group flex items-center gap-4 p-3.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] hover:border-gomill-mustard/40 transition-colors"
              >
                <img
                  src={g.img}
                  alt={`SDG ${g.n} ${g.title}`}
                  className="w-20 h-20 object-contain rounded-md shrink-0"
                />
                <p className="flex-1 min-w-0 text-white/85 text-[13px] md:text-sm leading-relaxed">
                  {g.desc}
                </p>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/50">© {new Date().getFullYear()} GoMill. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/50">
            <Link to="/privacy" className="hover:text-gomill-mustard" data-testid="footer-privacy">Privacy Policy</Link>
            <span className="flex items-center gap-1.5">
              Made with <span className="text-red-400 text-sm leading-none">♥</span> in India
            </span>
            <span className="hidden md:inline text-white/20">·</span>
            <span>
              Design &amp; Development by{" "}
              <a
                href="https://www.reachvel.com"
                target="_blank"
                rel="noreferrer"
                className="text-gomill-mustard hover:underline font-semibold"
                data-testid="footer-reachvel"
              >
                Reachvel
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
